/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed } from "mobx";
import { VerbIdProps } from "../constants/enums";
import { updateComponentProperties } from "../utils/APIUtils";

class ContextMenuStore {
  @observable menuItems;
  @observable xPosition;
  @observable yPosition;
  @observable classNamesToDisableContentMenuOn;
  @observable contextMenuTriggerEventObj;
  @observable componentProperties;
  @observable contextMenuHandle;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.menuItems = [];
    this.xPosition = -1;
    this.yPosition = -1;
    this.classNamesToDisableContentMenuOn = [];
    this.componentProperties = new Map();
    this.contextMenuHandle = null;
    Object.values(VerbIdProps).forEach(verb => this.componentProperties.set(verb, { visible: true }));

    // non-observables
    this.contextMenuStyle = {};
    this.trapFocus = true;
    this.contextMenuItemStyle = {};
    this.triggerType = undefined;
    this.triggerPageIndex = -1;
    this.triggerBBox = undefined;
    this.pageZoomLevel = undefined;
    this.isContextMenu = false;
    this.placement = "up";
    this.shouldTriggerContextMenuOnTextSelection = true;
    this.contextMenuTriggerEventObj = null;
  }

  @action
  setPosition(xPosition = -1, yPosition = -1) {
    this.xPosition = xPosition;
    this.yPosition = yPosition;
  }

  @action
  setTriggerType(type) {
    this.triggerType = type;
  }

  @action
  setDefaultPlacement(placement = "up") {
    const allowedPlacement = ["up", "down", "topRight"];
    allowedPlacement.includes(placement) && (this.placement = placement);
  }

  @action
  setTriggerBox(bbox) {
    this.triggerBBox = bbox;
  }

  @action
  setTriggerPage(pageIndex) {
    this.triggerPageIndex = pageIndex;
  }

  @action
  setPageZoomLevel(zoomLevel) {
    this.pageZoomLevel = zoomLevel;
  }

  @action
  setIsContextMenu(isContextMenu) {
    this.isContextMenu = isContextMenu;
  }

  @action
  setShouldTriggerContextMenuOnTextSelection(value) {
    this.shouldTriggerContextMenuOnTextSelection = value;
  }

  @action
  addMenuItems(menuItems) {
    this.menuItems.replace(menuItems);
  }

  @action
  clearMenuItems() {
    this.menuItems.clear();
    this.setPosition(-1, -1);
    this.placement = "up";
    this.triggerType = undefined;
    this.contextMenuTriggerEventObj = null;
  }

  @action
  addClassNameToDisableContextMenuOn(className) {
    if (this.classNamesToDisableContentMenuOn.indexOf(className) === -1) {
      this.classNamesToDisableContentMenuOn.push(className);
    }
  }

  @action
  setContextMenuStyle(style) {
    this.contextMenuStyle = style;
  }

  @action
  setTrapFocus(val) {
    this.trapFocus = val;
  }

  @action
  setContextMenuItemStyle(style) {
    this.contextMenuItemStyle = style;
  }

  @action
  setContextMenuTriggerEventObj(contextMenuTriggerEventObj) {
    this.contextMenuTriggerEventObj = contextMenuTriggerEventObj;
  }

  @action.bound
  setComponentProperties(propName, config) {
    updateComponentProperties(this.componentProperties, VerbIdProps[propName], config);
  }

  @action
  setContextMenuHandle(val) {
    this.contextMenuHandle = val;
  }

  @computed
  get disabledVerbs() {
    const props = [];
    for (const [key, value] of this.componentProperties) {
      if (!value.visible) props.push(key);
    }
    return props;
  }
}

export { ContextMenuStore };
